import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Card, Table, Empty, Tag, Button, Typography, Drawer, Modal,
  Space, Tooltip, Alert, App, Spin, DatePicker, Row, Col, Input,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import {
  ReloadOutlined, FileTextOutlined, FileOutlined,
  CopyOutlined, DownloadOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ForwardOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useEvent } from '../../hooks/useEvent';
import { useTheme } from '../../contexts/useTheme';
import stationIngestService, {
  type StationIngestExecution,
  type IngestStatus,
  type IngestLogEntry,
  type IngestExecutionFilter,
} from '../../api/stationIngestService';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const { Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_CONFIG: Record<IngestStatus, { color: string; label: string; icon: React.ReactNode }> = {
  succeeded: { color: 'success', label: 'Succeeded', icon: <CheckCircleOutlined /> },
  failed: { color: 'error', label: 'Failed', icon: <CloseCircleOutlined /> },
  skipped: { color: 'default', label: 'Skipped', icon: <ForwardOutlined /> },
};

const PAGE_SIZE = 50;

function createTextColumnFilter<T>(
  placeholder: string,
  getText: (record: T) => string | null,
): Pick<ColumnsType<T>[number], 'filterDropdown' | 'onFilter'> {
  return {
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          placeholder={placeholder}
          value={selectedKeys[0] as string}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm({ closeDropdown: true })}
          size="small"
          style={{ marginBottom: 8, display: 'block' }}
          allowClear
        />
        <Space>
          <Button type="primary" size="small" onClick={() => confirm({ closeDropdown: true })}>
            Filter
          </Button>
          <Button
            size="small"
            onClick={() => {
              clearFilters?.();
              confirm({ closeDropdown: true });
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    onFilter: (value, record) => {
      const text = getText(record as T);
      if (!text) return false;
      return text.toLowerCase().includes(String(value).toLowerCase());
    },
  };
}

function matchesSelectedEvent(
  execution: { season_year: number | null; circuit_id: string | null; start_date: string | null },
  event: { season_year: number; circuit_id: string; start_date: string },
): boolean {
  return execution.season_year === event.season_year
    && execution.circuit_id === event.circuit_id
    && (execution.start_date?.slice(0, 10) === event.start_date.slice(0, 10));
}

function parseCsvTable(csv: string): { headers: string[]; rows: Record<string, string>[] } {
  const lines = csv.trim().split('\n');
  if (lines.length === 0) return { headers: [], rows: [] };

  const headers = lines[0].split(',').map(h => h.trim());
  const rows = lines.slice(1)
    .filter(line => line.trim())
    .map((line, idx) => {
      const values = line.split(',').map(v => v.trim());
      const row: Record<string, string> = { key: String(idx) };
      headers.forEach((header, i) => {
        row[header] = values[i] ?? '';
      });
      return row;
    });

  return { headers, rows };
}

export const StationIngestPage = () => {
  const { selectedEvent } = useEvent();
  const { theme } = useTheme();
  const { message } = App.useApp();

  const [executions, setExecutions] = useState<StationIngestExecution[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  // Filters (default date range: last 7 days)
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>(
    [dayjs().startOf('day'), dayjs().endOf('day')]
  );

  // Log drawer
  const [logDrawerOpen, setLogDrawerOpen] = useState(false);
  const [logExecution, setLogExecution] = useState<StationIngestExecution | null>(null);
  const [logEntries, setLogEntries] = useState<IngestLogEntry[]>([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);

  // CSV modal
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [csvExecution, setCsvExecution] = useState<StationIngestExecution | null>(null);
  const [csvContent, setCsvContent] = useState<string | null>(null);
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);

  // Table container ref for dynamic scroll height
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableScrollY, setTableScrollY] = useState<number | undefined>(undefined);

  const TABLE_SCROLL_RESERVE_PX = 80;
  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const updateScrollY = () => {
      if (!el.isConnected) return;
      const height = el.clientHeight;
      setTableScrollY(Math.max(200, height - TABLE_SCROLL_RESERVE_PX));
    };
    updateScrollY();
    const ro = new ResizeObserver(updateScrollY);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fetchExecutions = useCallback(async (currentPage: number = 1) => {
    setLoading(true);

    const filter: IngestExecutionFilter = {
      executed_after: dateRange[0].startOf('day').toISOString(),
      executed_before: dateRange[1].endOf('day').toISOString(),
    };

    try {
      const response = await stationIngestService.getExecutions(
        filter,
        PAGE_SIZE,
        (currentPage - 1) * PAGE_SIZE,
      );

      if (response.success && response.data) {
        setExecutions((response.data as unknown as StationIngestExecution[]) || []);
        setTotal((response.raw as any)?.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch ingest executions:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    if (selectedEvent) {
      const today = dayjs();
      const start = dayjs(selectedEvent.start_date);
      const end = dayjs(selectedEvent.end_date);
      const isActive = today.isAfter(start.startOf('day')) && today.isBefore(end.endOf('day'));

      if (isActive) {
        setDateRange([today.startOf('day'), today.endOf('day')]);
      } else {
        setDateRange([start.startOf('day'), start.endOf('day')]);
      }
    } else {
      setDateRange([dayjs().startOf('day'), dayjs().endOf('day')]);
    }
  }, [selectedEvent]);

  useEffect(() => {
    setPage(1);
    fetchExecutions(1);
  }, [fetchExecutions]);

  const fetchLogs = useCallback(async (execution: StationIngestExecution) => {
    setLogExecution(execution);
    setLogDrawerOpen(true);
    setLogLoading(true);
    setLogError(null);
    setLogEntries([]);

    try {
      const response = await stationIngestService.getExecutionLogs(execution.id);
      if (response.success && response.data) {
        setLogEntries((response.data as unknown as IngestLogEntry[]) || []);
      } else {
        setLogError(response.errorData?.message || 'Failed to fetch logs');
      }
    } catch (err) {
      setLogError(err instanceof Error ? err.message : 'Failed to fetch logs');
    } finally {
      setLogLoading(false);
    }
  }, []);

  const fetchCsv = useCallback(async (execution: StationIngestExecution) => {
    setCsvExecution(execution);
    setCsvModalOpen(true);
    setCsvLoading(true);
    setCsvError(null);
    setCsvContent(null);
    setCsvFileName(execution.file_path.split('/').pop() || 'station-data.csv');

    try {
      const response = await stationIngestService.getExecutionFile(execution.id);
      if (response.success && response.data != null) {
        setCsvContent(String(response.data));
        const raw = response.raw as { file_name?: string } | undefined;
        if (raw?.file_name) {
          setCsvFileName(raw.file_name);
        }
      } else {
        setCsvError(response.errorData?.message || 'Failed to fetch CSV file');
      }
    } catch (err) {
      setCsvError(err instanceof Error ? err.message : 'Failed to fetch CSV file');
    } finally {
      setCsvLoading(false);
    }
  }, []);

  const downloadCsv = useCallback((content: string, fileName: string) => {
    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const csvTable = useMemo(() => {
    if (!csvContent) return { columns: [] as ColumnsType<Record<string, string>>, rows: [] as Record<string, string>[] };

    const { headers, rows } = parseCsvTable(csvContent);
    const columns: ColumnsType<Record<string, string>> = headers.map(header => ({
      title: header,
      dataIndex: header,
      key: header,
      ellipsis: true,
      width: header.toLowerCase() === 'datetime' ? 170 : 120,
      render: (val: string) => val || <Text type="secondary">-</Text>,
    }));

    return { columns, rows };
  }, [csvContent]);

  const tableData = useMemo(() => {
    if (!selectedEvent) return [];
    return executions.filter((execution) => matchesSelectedEvent(execution, selectedEvent));
  }, [executions, selectedEvent]);

  const stationFilterOptions = useMemo(
    () => Array.from(
      new Set(tableData.map(e => e.station_reference).filter(Boolean) as string[])
    )
      .sort((a, b) => Number(a) - Number(b))
      .map(s => ({ text: `S${s}`, value: s })),
    [tableData],
  );

  const columns: ColumnsType<StationIngestExecution> = useMemo(() => [
    {
      title: 'Time (UTC)',
      dataIndex: 'executed_at',
      key: 'executed_at',
      width: 170,
      render: (t: string) => dayjs(t).utc().format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'Station',
      dataIndex: 'station_reference',
      key: 'station_reference',
      width: 80,
      filters: stationFilterOptions,
      onFilter: (value, record) => record.station_reference === value,
      render: (ref: string | null) => ref ? <Tag>S{ref}</Tag> : <Text type="secondary">-</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      filters: [
        { text: 'Succeeded', value: 'succeeded' },
        { text: 'Failed', value: 'failed' },
        { text: 'Skipped', value: 'skipped' },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status: IngestStatus) => {
        const cfg = STATUS_CONFIG[status];
        return <Tag color={cfg.color} icon={cfg.icon}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Rows',
      key: 'rows',
      width: 80,
      render: (_: unknown, record: StationIngestExecution) => {
        if (record.rows_inserted != null) {
          return <Text>{record.rows_inserted}</Text>;
        }
        return <Text type="secondary">-</Text>;
      },
    },
    {
      title: 'Duration',
      dataIndex: 'duration_ms',
      key: 'duration_ms',
      width: 90,
      render: (ms: number) => <Text>{ms}ms</Text>,
    },
    {
      title: 'File',
      dataIndex: 'file_path',
      key: 'file_path',
      ellipsis: true,
      ...createTextColumnFilter<StationIngestExecution>(
        'Filter by file name',
        (record) => record.file_path.split('/').pop() || null,
      ),
      render: (path: string) => (
        <Tooltip title={path}>
          <Text code style={{ fontSize: 11 }}>{path.split('/').pop()}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Error',
      dataIndex: 'error_message',
      key: 'error_message',
      width: 200,
      ellipsis: true,
      ...createTextColumnFilter<StationIngestExecution>(
        'Filter by error text',
        (record) => record.error_message,
      ),
      render: (err: string | null) => err ? (
        <Tooltip title={err}>
          <Text type="danger" ellipsis style={{ maxWidth: 180 }}>
            <ExclamationCircleOutlined /> {err}
          </Text>
        </Tooltip>
      ) : '-',
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      fixed: 'right',
      render: (_: unknown, record: StationIngestExecution) => (
        <Space size={0}>
          <Tooltip title="View CSV">
            <Button
              type="link"
              size="small"
              icon={<FileOutlined />}
              onClick={() => fetchCsv(record)}
            />
          </Tooltip>
          <Tooltip title="View Logs">
            <Button
              type="link"
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => fetchLogs(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ], [fetchCsv, fetchLogs, stationFilterOptions]);

  const formatLogText = (entries: IngestLogEntry[]) =>
    entries
      .map(e => {
        const ts = e.timestamp ? new Date(e.timestamp).toISOString() : '';
        return `${ts}  ${e.severity.padEnd(7)}  ${e.message}`;
      })
      .join('\n');

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0, gap: 0 }}>
      {/* Title Card */}
      <Card
        styles={{ body: { padding: '8px 12px' } }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span>Station Ingest</span>
            {selectedEvent && (
              <span style={{
                fontSize: '12px',
                fontWeight: 'normal',
                color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.65)',
                backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                padding: '2px 10px',
                borderRadius: '4px',
              }}>
                {selectedEvent.name}
              </span>
            )}
          </div>
        }
        extra={
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Tooltip title="Refresh">
              <Button
                icon={<ReloadOutlined spin={loading} />}
                onClick={() => fetchExecutions(page)}
                size="small"
                type="text"
              />
            </Tooltip>
          </div>
        }
      >
        {!selectedEvent && (
          <Empty description="Select an event to view ingest executions" />
        )}
      </Card>

      {/* Table Card */}
      <Card
        style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 0 }}
        styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '8px 12px' } }}
      >
        {/* Filters */}
        <Row gutter={16} align="middle" style={{ marginBottom: 12, flexShrink: 0 }}>
          <Col>
            <Space size="small">
              <Text type="secondary">Date:</Text>
              <RangePicker
                value={dateRange}
                onChange={(v) => {
                  if (v && v[0] && v[1]) {
                    setDateRange([v[0], v[1]]);
                  }
                }}
                allowClear={false}
                size="small"
                style={{ width: 240 }}
              />
            </Space>
          </Col>
        </Row>

        <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0 }}>
          <Table<StationIngestExecution>
            columns={columns}
            dataSource={tableData}
            rowKey="id"
            loading={loading}
            size="small"
            scroll={{ x: 1000, ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total,
              showSizeChanger: false,
              showTotal: (t) => `${t} executions`,
              onChange: (p) => {
                setPage(p);
                fetchExecutions(p);
              },
            }}
            locale={{
              emptyText: (
                <Empty
                  description={
                    selectedEvent
                      ? 'No ingest executions found for this event'
                      : 'Select an event to view ingest executions'
                  }
                />
              ),
            }}
          />
        </div>
      </Card>

      {/* CSV Modal */}
      <Modal
        title={
          <Space>
            <FileOutlined />
            <span>{csvFileName || 'CSV File'}</span>
            {csvExecution && (
              <Tag color={STATUS_CONFIG[csvExecution.status]?.color}>
                S{csvExecution.station_reference || '?'}
              </Tag>
            )}
          </Space>
        }
        open={csvModalOpen}
        onCancel={() => setCsvModalOpen(false)}
        width="80%"
        footer={[
          <Button key="close" onClick={() => setCsvModalOpen(false)}>
            Close
          </Button>,
          csvContent && csvFileName ? (
            <Button
              key="download"
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => downloadCsv(csvContent, csvFileName)}
            >
              Download
            </Button>
          ) : null,
        ]}
        styles={{
          body: {
            maxHeight: '70vh',
            overflowY: 'auto',
          },
        }}
      >
        {csvExecution && (
          <div style={{ marginBottom: 12, fontSize: 12, color: theme === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)' }}>
            <div>Path: <Text code style={{ fontSize: 11 }}>{csvExecution.file_path}</Text></div>
          </div>
        )}

        {csvLoading && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 12, color: theme === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)' }}>
              Loading CSV from storage...
            </div>
          </div>
        )}

        {csvError && (
          <Alert
            type="error"
            message="Failed to load CSV"
            description={csvError}
            showIcon
          />
        )}

        {!csvLoading && !csvError && csvContent && (
          <>
            <div style={{ marginBottom: 12, fontSize: 12 }}>
              <Text type="secondary">{csvTable.rows.length} rows</Text>
            </div>
            <Table
              columns={csvTable.columns}
              dataSource={csvTable.rows}
              size="small"
              scroll={{ x: 'max-content', y: '55vh' }}
              pagination={{
                pageSize: 50,
                showSizeChanger: true,
                pageSizeOptions: ['25', '50', '100', '200'],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows`,
              }}
            />
          </>
        )}
      </Modal>

      {/* Logs Drawer */}
      <Drawer
        title={
          <Space>
            <FileTextOutlined />
            <span>Ingest Logs</span>
            {logExecution && (
              <Tag color={STATUS_CONFIG[logExecution.status]?.color}>
                S{logExecution.station_reference || '?'}
              </Tag>
            )}
          </Space>
        }
        placement="right"
        size="large"
        open={logDrawerOpen}
        onClose={() => setLogDrawerOpen(false)}
        extra={
          <Space size="small">
            {logEntries.length > 0 && (
              <>
                <Tooltip title="Copy to clipboard">
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => {
                      navigator.clipboard.writeText(formatLogText(logEntries));
                      message.success('Logs copied to clipboard');
                    }}
                  />
                </Tooltip>
                <Tooltip title="Save as file">
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => {
                      const blob = new Blob([formatLogText(logEntries)], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `ingest-${logExecution?.pubsub_message_id || logExecution?.id?.slice(0, 8)}-logs.txt`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                  />
                </Tooltip>
              </>
            )}
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => logExecution && fetchLogs(logExecution)}
              loading={logLoading}
            >
              Refresh
            </Button>
          </Space>
        }
      >
        {logExecution && (
          <div style={{ marginBottom: 12, fontSize: 12, color: theme === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)' }}>
            <div>File: <Text code style={{ fontSize: 11 }}>{logExecution.file_path}</Text></div>
            <div>Message ID: <Text code style={{ fontSize: 11 }}>{logExecution.pubsub_message_id}</Text></div>
            <div>
              Executed: {dayjs(logExecution.executed_at).utc().format('YYYY-MM-DD HH:mm:ss')} UTC
              {' '}({logExecution.duration_ms}ms)
            </div>
          </div>
        )}

        {logLoading && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 12, color: theme === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)' }}>Fetching logs from Cloud Logging...</div>
          </div>
        )}

        {logError && (
          <Alert
            type="error"
            message="Failed to load logs"
            description={logError}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {!logLoading && !logError && logEntries.length === 0 && (
          <Empty description="No log entries found" />
        )}

        {!logLoading && logEntries.length > 0 && (
          <div
            style={{
              fontFamily: 'monospace',
              fontSize: '12px',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              backgroundColor: theme === 'dark' ? '#1a1a1a' : '#f5f5f5',
              padding: '12px',
              borderRadius: '6px',
              maxHeight: 'calc(100vh - 240px)',
              overflow: 'auto',
            }}
          >
            {logEntries.map((entry, idx) => {
              const ts = entry.timestamp
                ? new Date(entry.timestamp).toLocaleTimeString('en-US', {
                    hour12: false,
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    fractionalSecondDigits: 3,
                  } as Intl.DateTimeFormatOptions)
                : '';
              const severityColor =
                entry.severity === 'ERROR' ? '#f5222d' :
                entry.severity === 'WARNING' ? '#faad14' :
                entry.severity === 'INFO' ? '#1890ff' :
                theme === 'dark' ? '#d9d9d9' : '#595959';

              return (
                <div key={idx} style={{ marginBottom: '2px' }}>
                  <span style={{ color: theme === 'dark' ? '#8c8c8c' : '#8c8c8c', marginRight: '8px' }}>{ts}</span>
                  <span style={{ color: severityColor }}>{entry.message}</span>
                </div>
              );
            })}
          </div>
        )}
      </Drawer>
    </div>
  );
};
